import { useParams, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { getVisit, getClinicVisitType, getVisitEntriesTimeline, updateVisitEntry, getVisitMedicines } from "../../../apis/visit";
import { Visit } from "../../../interfaces/Visit";
import { format } from "date-fns";
import { useAppDispatch } from "../../../hooks";
import { changeVisit } from "../../../features/visit/visitSlice";
import { getPatient } from "../../../apis/patient";
import { useNotification } from "../../../NotificationContext";
import LoaderComponent from "../../../components/LoaderComponent";
import Modal from "../../../components/Modal";
import IPDEntryCard from "./IPDEntryCard";
import IPDEntryForm from "./IPDEntryForm";
import IPDAdmissionForm from "./forms/IPDAdmissionForm";
import PaymentModal from "../../../components/PaymentModal";
import AddLabOrder from "../../laboratory/AddLabOrder";
import { getLayout, getLayoutById } from "../../../apis/visit";
import ServiceModal from "./forms/ServicesForm";
import IPDBillingModal from "./forms/IPDBillingModal";
import GeneralCommentForm from "./forms/GeneralCommentForm";

import ViewLabOrder from "../../laboratory/ViewLabOrder";
import { Modal as AntModal } from "antd";
import EpisodeContextBanner from "../../../components/EpisodeContextBanner";

const IPDTimeline = () => {

  const [visit, setVisit] = useState<Visit>();
  const [patient, setPatient] = useState<any>(null);
  const [clinicVisitType, setClinicVisitType] = useState<any>(null);
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoader, setLoader] = useState<boolean>(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const [entryType, setEntryType] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [, setClinicVisitTypeLayout] = useState<any>(null);
  const [, setVisitTypeLayout] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLabOrderModal, setShowLabOrderModal] = useState(false);
  const [selectedLabOrderId, setSelectedLabOrderId] = useState<number | undefined>(undefined);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);

  const [, setIpdLayouts] = useState<{
    assessment: any;
    doctor_rounds: any;
    nursing: any;
  } | null>(null);
  const [, setIsLoading] = useState<boolean>(false);
  const [timelineEntries, setTimelineEntries] = useState<any[]>([]);
  const [visitMedicines, setVisitMedicines] = useState<any[]>([]);
  const [showMedList, setShowMedList] = useState(false);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState<string>("all");
  const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsModalEntry, setCommentsModalEntry] = useState<any>(null);
  const [openLabReport, setOpenLabReport] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | undefined>(undefined);
  // Effects are declared after callbacks to avoid use-before-define lint errors.

  const fetchPatient = (patientId: any) => {
    getPatient(patientId).then((res: any) => {
      setPatient(res.data.data);
    });
  };

  const fetchClinicVisitType = (id: any) => {
    getClinicVisitType(id).then((res: any) => {
      setClinicVisitType(res.data);
    });
  };

  // Process IPD layouts from API response (converts snake_case to camelCase)
  const processIPDLayouts = useCallback((layoutData: any, flowTypeId: number | string) => {
    const processed: any = {
      assessment: null,
      doctor_rounds: null,
      nursing: null,
    };

    console.log("layoutData", layoutData);

    // Handle both camelCase (doctorRounds) and snake_case (doctor_rounds) formats
    const entryTypeMap: Record<string, string> = {
      "assessment": "assessment",
      "doctorRounds": "doctor_rounds",
      "doctor_rounds": "doctor_rounds",
      "nursing": "nursing",
    };

    Object.keys(entryTypeMap).forEach((key) => {
      const entryType = entryTypeMap[key];
      const entry = layoutData[key] || layoutData[entryType];

      if (entry) {
        // Convert grid_items (snake_case from API) to gridItems (camelCase for frontend)
        const gridItems = entry.grid_items || entry.gridItems || [];
        const layout = entry.layout || [];

        // Ensure layout items have string keys (i should be string)
        const processedLayout = layout.map((item: any) => ({
          ...item,
          i: String(item.i || item.id || ""),
        }));

        processed[entryType] = {
          gridItems: gridItems,
          layout: processedLayout,
        };
      }
    });

    setIpdLayouts(processed);

    // Save to localStorage for IPDEntryForm to use
    // Use clinicVisitTypeId as flowTypeId (since IPD visit types are the flow types)
    const flowId =
      flowTypeId || visit?.clinicVisitTypeId || visit?.meta?.ipdFlowTypeId || 1;
    const layoutsToSave: any = {};
    Object.keys(processed).forEach((key) => {
      if (
        processed[key] &&
        processed[key].gridItems &&
        processed[key].gridItems.length > 0
      ) {
        layoutsToSave[key] = processed[key];
      }
    });

    if (Object.keys(layoutsToSave).length > 0) {
      localStorage.setItem(
        `ipd_layouts_${flowId}`,
        JSON.stringify(layoutsToSave)
      );
      console.log(`Saved IPD layouts for flow type ${flowId}:`, layoutsToSave);
    }
  }, [visit?.clinicVisitTypeId, visit?.meta?.ipdFlowTypeId]);

  const getVisitTypeLayout = useCallback((id: any, data: any) => {
    setIsLoading(true);
    getLayout(id).then((res: any) => {
      setClinicVisitTypeLayout(res.data);
      // Process IPD layouts if they exist
      // IPD format: { layout: { layout: { assessment: {...}, doctorRounds: {...}, nursing: {...} } } }
      const layout = res.data?.layout;
      if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
        // Check if it has nested layout structure (IPD format)
        if (layout.layout && typeof layout.layout === 'object' && !Array.isArray(layout.layout)) {
          // Check if it has IPD entry types (assessment, doctorRounds/doctor_rounds, nursing)
          const ipdLayoutData = layout.layout;
          if ('assessment' in ipdLayoutData || 'doctorRounds' in ipdLayoutData || 'doctor_rounds' in ipdLayoutData || 'nursing' in ipdLayoutData) {
            processIPDLayouts(ipdLayoutData, id);
          }
        } else if ('assessment' in layout || 'doctorRounds' in layout || 'doctor_rounds' in layout || 'nursing' in layout) {
          // Fallback: check if layout itself has IPD entry types (for backward compatibility)
          processIPDLayouts(layout, id);
        }
      }
      setIsLoading(false);
    }).catch((error) => {
      console.error("Failed to load layout:", error);
      setIsLoading(false);
    });
  }, [processIPDLayouts]);

  const getVisitTypeLayoutById = useCallback((id: any, data: any) => {
    setIsLoading(true);
    getLayoutById(id).then((res: any) => {
      setVisitTypeLayout(res.data);
      // Process IPD layouts if they exist
      // IPD format: { layout: { layout: { assessment: {...}, doctorRounds: {...}, nursing: {...} } } }
      const layout = res.data?.layout;
      if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
        // Get clinic_visit_type_id from the layout record to use as flowTypeId
        const clinicVisitTypeId = res.data?.clinic_visit_type_id || visit?.clinicVisitTypeId;

        // Check if it has nested layout structure (IPD format)
        if (layout.layout && typeof layout.layout === 'object' && !Array.isArray(layout.layout)) {
          // Check if it has IPD entry types (assessment, doctorRounds/doctor_rounds, nursing)
          const ipdLayoutData = layout.layout;
          if ('assessment' in ipdLayoutData || 'doctorRounds' in ipdLayoutData || 'doctor_rounds' in ipdLayoutData || 'nursing' in ipdLayoutData) {
            processIPDLayouts(ipdLayoutData, clinicVisitTypeId);
          }
        } else if ('assessment' in layout || 'doctorRounds' in layout || 'doctor_rounds' in layout || 'nursing' in layout) {
          // Fallback: check if layout itself has IPD entry types (for backward compatibility)
          processIPDLayouts(layout, clinicVisitTypeId);
        }
      }
      setIsLoading(false);
    }).catch((error) => {
      console.error("Failed to load layout by ID:", error);
      setIsLoading(false);
    });
  }, [processIPDLayouts, visit?.clinicVisitTypeId]);

  const fetchVisit = useCallback(async (id: any) => {
    setLoader(true);
    await getVisit(id).then(async (res: any) => {
      dispatch(changeVisit(res));
      setVisit(res);
      setLoader(false);
      // setPatient(res.patient);
      const patientId = params.patientId || res.patient?.id;
      if (patientId) {
        fetchPatient(patientId);
      } else {
        setPatient(res.patient);
      }
      // Fetch clinic visit type if available from visit but not in params
      if (res.clinicVisitTypeId && !params.clinicVisitTypeId) {
        fetchClinicVisitType(res.clinicVisitTypeId);
      }
      // After visit is loaded, fetch layout if clinicVisitTypeId is available
      if (res.clinicVisitTypeId && !res.clinicVisitTypeLayoutId) {
        getVisitTypeLayout(res.clinicVisitTypeId, res.meta);
      }
      // Fetch timeline entries
      if (res.id) {
        fetchTimelineEntries(res.id);
        getVisitMedicines(res.id)
          .then((medRes: any) => {
            setVisitMedicines(medRes?.data?.data ?? []);
          })
          .catch(() => {});
      }
    });
  }, [dispatch, getVisitTypeLayout, params.clinicVisitTypeId]);

  useEffect(() => {
    if (params.visitId) {
      fetchVisit(params.visitId);
    }
    // if (params.patientId) {
    //   fetchPatient(params.patientId);
    // }
    if (params.clinicVisitTypeId) {
      fetchClinicVisitType(params.clinicVisitTypeId);
      getVisitTypeLayout(parseInt(params.clinicVisitTypeId), {});
    }
  }, [params.visitId, params.clinicVisitTypeId, params.patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-show admission form if visit doesn't exist
  useEffect(() => {
    // Only show admission form if:
    // 1. No visitId in params (new admission)
    // 2. Visit is not loaded
    // 3. We have patientId and clinicVisitTypeId
    if (!params.visitId && !visit && params.patientId && params.clinicVisitTypeId) {
      setShowAdmissionForm(true);
    }
  }, [params.visitId, params.patientId, params.clinicVisitTypeId, visit]);

  useEffect(() => {
    if (!visit) return;
    if (visit.clinicVisitTypeLayoutId) {
      getVisitTypeLayoutById(visit.clinicVisitTypeLayoutId, visit.meta);
    } else if (visit.clinicVisitTypeId) {
      getVisitTypeLayout(visit.clinicVisitTypeId, visit.meta);
    }
  }, [visit?.clinicVisitTypeLayoutId, visit?.clinicVisitTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTimelineEntries = async (visitId: number) => {
    setIsLoadingTimeline(true);
    try {
      const response: any = await getVisitEntriesTimeline(visitId);
      // The API returns { data: [...] } structure
      const entries = response?.data || response?.entries || [];
      setTimelineEntries(entries);
    } catch (error) {
      console.error("Failed to fetch timeline entries:", error);
      setTimelineEntries([]);
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  const handleCommentsClick = (entry: any) => {
    setCommentsModalEntry(entry);
    setShowCommentsModal(true);
  };

  const handleCommentsModalClose = () => {
    setShowCommentsModal(false);
    setCommentsModalEntry(null);
  };

  const handleCommentsSave = async (data: { comments: string }) => {
    if (!visit?.id || !commentsModalEntry?.id) return;
    try {
      const formData = {
        ...(commentsModalEntry.data || {}),
        comments: data.comments,
      };
      await updateVisitEntry(visit.id, Number(commentsModalEntry.id), {
        form_data: formData,
      });
      showNotification("success", "Comments saved", "", "topRight");
      handleCommentsModalClose();
      fetchTimelineEntries(visit.id);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Failed to save comments";
      showNotification("error", msg, "", "topRight");
    }
  };

  const handleAddEntry = (type: string) => {
    // If visit doesn't exist and user clicks assessment, show admission form
    if (!visit && type === "assessment") {
      setShowAdmissionForm(true);
      return;
    }

    // If visit doesn't exist for other entry types, show message
    if (!visit) {
      showNotification(
        "warning",
        "Please complete admission first",
        "You need to create an admission before adding entries",
        "topRight"
      );
      return;
    }

    if (type === "payment") {
      setShowPaymentModal(true);
      return;
    }

    if (type === "billing") {
      setShowBillingModal(true);
      return;
    }

    if (type === "lab_request") {
      setShowLabOrderModal(true);
      return;
    }

    if (type === "services") {
      setShowServiceModal(true);
      return;
    }

    setEntryType(type);
    setEditingEntry(null);
    setShowEntryForm(true);

    console.log("State updated - entryType:", type, "showEntryForm: true");
  };

  const handleEditEntry = (entry: any) => {
    // If entry type is admission, open admission form with pre-filled values
    const normalizedType = String(entry?.type || "").toLowerCase();
    if (normalizedType.includes("admission")) {
      console.log("handleEditEntry admission entry:", entry);
      setEditingEntry(entry);
      setShowAdmissionForm(true);
      return;
    }

    setEditingEntry(entry);
    setEntryType(entry.type);
    setShowEntryForm(true);
  };

  const handleEntrySaved = () => {
    setShowEntryForm(false);
    setEntryType(null);
    setEditingEntry(null);
    if (params.visitId) {
      fetchVisit(params.visitId);
      // Also refresh timeline
      fetchTimelineEntries(parseInt(params.visitId));
    }
  };

  const handleAdmissionSaved = (savedVisit: any) => {
    setShowAdmissionForm(false);
    setEditingEntry(null);
    dispatch(changeVisit(savedVisit));
    setVisit(savedVisit);
    // Refresh timeline entries
    if (savedVisit.id) {
      fetchTimelineEntries(savedVisit.id);
      // Only navigate if it's a new visit
      if (!params.visitId) {
        navigate(`/ipd/visits/${savedVisit.id}`);
      }
    }
  };

  const handleCancelAdmission = () => {
    setShowAdmissionForm(false);
    setEditingEntry(null);
  };

  const handleCancelEntry = () => {
    setShowEntryForm(false);
    setEntryType(null);
    setEditingEntry(null);
  };

  // Get IPD entries from timeline API, sorted by created_at (latest first)
  const getIPDEntries = () => {
    if (!timelineEntries || timelineEntries.length === 0) return [];
    // Timeline entries should already be sorted in descending order by backend
    // But we'll ensure they're sorted by created_at descending (latest first)
    return [...timelineEntries].sort(
      (a: any, b: any) =>
        new Date(b.created_at || b.createdAt || 0).getTime() -
        new Date(a.created_at || a.createdAt || 0).getTime()
    );
  };

  // Extract most recent vitals from timeline entries
  const getLatestVitals = () => {
    const sorted = [...timelineEntries].sort(
      (a: any, b: any) =>
        new Date(b.createdAt || b.created_at || 0).getTime() -
        new Date(a.createdAt || a.created_at || 0).getTime()
    );
    for (const entry of sorted) {
      const vitals = (entry.formData || entry.form_data)?.vitals;
      if (vitals && (vitals.bp || vitals.pulse || vitals.temp || vitals.spo2)) {
        return {
          vitals,
          asOf: entry.createdAt || entry.created_at,
        };
      }
    }
    return null;
  };

  const getAdmissionDayCount = () => {
    const admissionDate = visit?.meta?.admissionDate || visit?.date;
    if (!admissionDate) return null;
    const diff = Math.floor(
      (Date.now() - new Date(admissionDate).getTime()) / (1000 * 60 * 60 * 24)
    );
    return diff + 1;
  };

  const getPatientInitials = () => {
    const first = patient?.firstName ?? "";
    const last = patient?.lastName ?? "";
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  };

  const ipdEntries = getIPDEntries();
  const resolvedPatientId = params.patientId
    ? parseInt(params.patientId)
    : visit?.patientId;
  const resolvedClinicVisitTypeId = params.clinicVisitTypeId
    ? parseInt(params.clinicVisitTypeId)
    : visit?.clinicVisitTypeId;

  return (
    <div className="flex flex-col flex-grow">
      <main className="flex flex-col flex-grow bg-[#ECEFFF] rounded-md">
        {/* ── Sticky header ── */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">

          {/* ── Top bar — always visible, expandable ── */}
          <div className="border-b border-gray-100">
            <div className="flex items-start justify-between px-4 py-2.5">
              <div className="flex flex-col gap-1">
                {/* Name row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 capitalize">
                    {patient?.firstName} {patient?.lastName}
                  </span>
                  {patient?.registrationNumber && (
                    <span className="text-gray-400 text-sm">({patient.registrationNumber})</span>
                  )}
                  {(visit?.clinicVisitType?.visitType?.name || clinicVisitType?.name) && (
                    <span className="px-2 py-0.5 rounded border border-gray-300 text-gray-600 text-xs">
                      {visit?.clinicVisitType?.visitType?.name || clinicVisitType?.name}
                    </span>
                  )}
                  {getAdmissionDayCount() && (
                    <span className="px-2 py-0.5 rounded border border-amber-300 text-amber-700 text-xs font-medium">
                      Day {getAdmissionDayCount()}
                    </span>
                  )}
                  {visit?.status && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      (visit as any).status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}>
                      {(visit as any).status === "active" ? "Active" : (visit as any).status}
                    </span>
                  )}
                </div>
                {/* Always-visible admission info */}
                <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
                  {visit?.chiefComplaint && (
                    <span className="font-medium text-gray-700">{visit.chiefComplaint}</span>
                  )}
                  {visit?.user?.firstName && (
                    <span>
                      Dr. {[visit.user.firstName, visit.user.middleName, visit.user.lastName].filter(Boolean).join(" ")}
                    </span>
                  )}
                  {visit?.room?.name && (
                    <span>
                      {visit.room.name}
                      {visit?.meta?.bedId ? `, Bed ${visit.meta.bedId}` : ""}
                    </span>
                  )}
                  {visit?.meta?.admissionDate && (
                    <span>
                      Admitted {new Date(visit.meta.admissionDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPatientDetails((p) => !p)}
                className="flex-shrink-0 mt-0.5 px-2 py-1 border border-gray-300 rounded text-xs text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors"
              >
                {showPatientDetails ? "Hide ▲" : "Details ▼"}
              </button>
            </div>

            {/* Expandable patient details */}
            {showPatientDetails && (
              <div className="px-4 pb-3 border-t border-gray-100 pt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                {/* Chief complaint */}
                {visit?.chiefComplaint && (
                  <div className="col-span-2 flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Chief Complaint</span>
                    <span className="text-gray-800 font-medium">{visit.chiefComplaint}</span>
                  </div>
                )}
                {/* Provisional diagnosis */}
                {(visit?.provisionalDiagnosis?.length ?? 0) > 0 && (
                  <div className="col-span-2 flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Diagnosis</span>
                    <span className="text-gray-700">{(visit?.provisionalDiagnosis ?? []).map((d: any) => d.label ?? d).join(", ")}</span>
                  </div>
                )}
                {/* Doctor */}
                {visit?.user?.firstName && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Doctor</span>
                    <span className="text-gray-700">
                      Dr. {[visit.user.firstName, visit.user.middleName, visit.user.lastName].filter(Boolean).join(" ")}
                    </span>
                  </div>
                )}
                {/* Room */}
                {visit?.room?.name && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Room</span>
                    <span className="text-gray-700">
                      {visit.room.name}
                      {visit?.meta?.bedId ? `, Bed ${visit.meta.bedId}` : ""}
                    </span>
                  </div>
                )}
                {/* Admission date */}
                {visit?.meta?.admissionDate && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Admitted</span>
                    <span className="text-gray-700">
                      {new Date(visit.meta.admissionDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      {visit?.meta?.admissionTime ? ` at ${visit.meta.admissionTime}` : ""}
                    </span>
                  </div>
                )}
                {/* Patient demographics */}
                {(patient?.age || patient?.gender || patient?.bloodGroup) && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Patient</span>
                    <span className="text-gray-700">
                      {[
                        patient?.age ? `${patient.age}y` : null,
                        patient?.gender === "male" ? "Male" : patient?.gender === "female" ? "Female" : (patient?.gender ?? null),
                        patient?.bloodGroup || null,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                )}
                {patient?.phone && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Phone</span>
                    <span className="text-gray-700">{patient.phone}</span>
                  </div>
                )}
                {patient?.email && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Email</span>
                    <span className="text-gray-700">{patient.email}</span>
                  </div>
                )}
                {patient?.birthDate && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Date of Birth</span>
                    <span className="text-gray-700">{new Date(patient.birthDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                )}
                {patient?.address && (
                  <div className="col-span-2 flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Address</span>
                    <span className="text-gray-700">{patient.address}</span>
                  </div>
                )}
                {visit?.meta?.paymentType && (
                  <div className="flex gap-1.5">
                    <span className="text-gray-400 w-28 flex-shrink-0">Payment</span>
                    <span className="text-gray-700 capitalize">
                      {visit.meta.paymentType}{visit?.meta?.policyNumber ? ` · Policy: ${visit.meta.policyNumber}` : ""}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Current vitals ── */}
          <div className="px-4 py-3 border-b border-gray-100">
            {(() => {
              const latest = getLatestVitals();
              const v = latest?.vitals ?? {};
              return (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Current Vitals</span>
                    {latest?.asOf && (
                      <span className="text-xs text-gray-400">
                        as of {new Date(latest.asOf).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} today
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "BP (mmHg)", value: v.bp },
                      { label: "Pulse (bpm)", value: v.pulse },
                      { label: "Temp (°C)", value: v.temp },
                      { label: "SpO2 (%)", value: v.spo2 },
                    ].map((vital) => (
                      <div key={vital.label} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                        <div className={`text-lg font-bold ${vital.value ? "text-gray-900" : "text-gray-300"}`}>
                          {vital.value ?? "—"}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{vital.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* ── Medications ── */}
          <div className="border-b border-gray-100">
            <button
              type="button"
              onClick={() => setShowMedList((p) => !p)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Active Medications</span>
                <span className="text-xs text-gray-500">
                  {visitMedicines.filter((m: any) => m.status === "active").length} ongoing
                </span>
              </div>
              <span className="text-gray-400 text-xs">{showMedList ? "▲" : "▼"}</span>
            </button>
            {showMedList && (
              <div className="divide-y divide-gray-100">
                {visitMedicines.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-gray-400">No medications prescribed.</div>
                ) : (
                  visitMedicines.map((m: any) => {
                    const inst = m.instructions ?? {};
                    const stopped = m.status === "stopped" || m.status === "complete";
                    const dose = inst.dose ?? (inst.power ? `${inst.power}${inst.powerUnit ?? inst.power_unit ?? ""}` : "");
                    const route = inst.route ?? "";
                    const freq = inst.frequency ?? "";
                    const notes = inst.notes ?? "";
                    const detailParts = [route, freq, notes].filter(Boolean).join(" · ");

                    const statusBadge = (() => {
                      switch (m.status) {
                        case "stopped":
                          return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-red-300 text-red-500 bg-red-50">Stopped</span>;
                        case "held":
                          return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-yellow-300 text-yellow-600 bg-yellow-50">Held</span>;
                        case "complete":
                          return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-gray-300 text-gray-500 bg-gray-50">Complete</span>;
                        default:
                          if (freq.toLowerCase().includes("sos") || inst.type === "sos")
                            return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-gray-300 text-gray-600 bg-gray-50">SOS</span>;
                          if (route.toLowerCase().includes("iv"))
                            return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-blue-300 text-blue-600 bg-blue-50">IV Active</span>;
                          return <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs border border-green-300 text-green-600 bg-green-50">Ongoing</span>;
                      }
                    })();

                    return (
                      <div key={m.id} className={`flex items-center justify-between gap-3 px-4 py-2.5 ${stopped ? "opacity-50" : "hover:bg-gray-50"}`}>
                        <div className="min-w-0">
                          <div className={`text-sm font-medium ${stopped ? "line-through text-gray-400" : "text-gray-900"}`}>
                            {m.medicineName}{dose ? ` ${dose}` : ""}
                          </div>
                          {detailParts && (
                            <div className={`text-xs mt-0.5 ${stopped ? "line-through text-gray-400" : "text-gray-500"}`}>
                              {detailParts}
                            </div>
                          )}
                        </div>
                        {statusBadge}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* ── Action buttons ── */}
          <div className="px-3 py-2.5 overflow-x-auto bg-white">
            <div className="flex gap-1.5 min-w-max flex-wrap">
              {[
                { label: "Doctor Round", type: "doctor_rounds" },
                { label: "Nursing Note", type: "nursing" },
                { label: "Pharmacy", type: "pharmacy_request" },
                { label: "Lab Report", type: "lab_request" },
                { label: "OT", type: "ot" },
                { label: "Billing", type: "billing" },
                { label: "Assessment", type: "assessment" },
                { label: "Comments", type: "general_comment" },
                { label: "Services", type: "services" },
              ].map(({ label, type }) => (
                <button
                  key={type}
                  onClick={() => handleAddEntry(type)}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors whitespace-nowrap"
                >
                  + {label}
                </button>
              ))}
              <button
                onClick={() => handleAddEntry("discharge")}
                className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                + Discharge
              </button>
            </div>
          </div>
        </div>

        {visit?.episode && <EpisodeContextBanner episode={visit.episode as any} />}

        <div className="overflow-y-auto flex-1 p-4">
          {/* Timeline heading + filter tabs */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Activity timeline</h2>
            <div className="flex gap-1.5">
              {[
                { key: "all", label: "All" },
                { key: "doctor_rounds", label: "Rounds" },
                { key: "nursing", label: "Nursing" },
                { key: "lab_request", label: "Lab" },
                { key: "ot", label: "OT" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setTimelineFilter(f.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    timelineFilter === f.key
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "border-gray-300 text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline entries */}
          {(isLoader || isLoadingTimeline) ? (
            <LoaderComponent />
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200" />
              <div className="space-y-6">
                {ipdEntries
                  .filter((entry: any) => {
                    if (timelineFilter === "all") return true;
                    const t = entry.entryType || entry.entry_type;
                    return t === timelineFilter;
                  })
                  .map((entry: any, index: number) => {
                    const transformedEntry = {
                      id: entry.id,
                      title: entry.title,
                      type: entry.entryType || entry.entry_type,
                      date: entry.createdAt || entry.created_at,
                      data: entry.formData || entry.form_data || {},
                      userId: entry.createdBy?.id || entry.created_by?.id,
                      clinicVisitTypeLayoutId:
                        entry.clinicVisitTypeLayoutId ||
                        entry.clinic_visit_type_layout_id,
                    };

                    const entryType = transformedEntry.type;
                    const createdByName =
                      entry.createdBy?.user?.name ??
                      entry.created_by?.user?.name ??
                      null;

                    const typeLabel: Record<string, string> = {
                      assessment: "Assessment",
                      doctor_rounds: "Doctor Round",
                      nursing: "Nursing Note",
                      pharmacy_request: "Pharmacy",
                      lab_request: "Lab Report",
                      room_transfer: "Room Transfer",
                      payment: "Payment",
                      general_comment: "General Comment",
                      discharge: "Discharge",
                      ot: "OT",
                      admission: "Admission",
                    };

                    const dotColor: Record<string, string> = {
                      doctor_rounds: "bg-indigo-500",
                      nursing: "bg-teal-500",
                      lab_request: "bg-yellow-500",
                      pharmacy_request: "bg-purple-500",
                      ot: "bg-orange-500",
                      assessment: "bg-blue-500",
                      discharge: "bg-red-500",
                      payment: "bg-green-500",
                      general_comment: "bg-gray-400",
                      admission: "bg-emerald-500",
                    };

                    const dateStr = (() => {
                      try {
                        return format(new Date(transformedEntry.date), "dd MMM yyyy, HH:mm");
                      } catch {
                        return transformedEntry.date;
                      }
                    })();

                    return (
                      <div key={entry.id || index} className="relative pl-6">
                        {/* Dot */}
                        <div className={`absolute left-0 top-1 w-2.5 h-2.5 rounded-full -translate-x-[4px] ${dotColor[entryType] ?? "bg-gray-400"}`} />

                        {/* Meta row — outside card */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1.5 flex-wrap">
                          <span className="font-medium text-gray-700">{dateStr}</span>
                          <span>·</span>
                          <span>{typeLabel[entryType] ?? entryType}</span>
                          {createdByName && (
                            <>
                              <span>·</span>
                              <span>{createdByName}</span>
                            </>
                          )}
                        </div>

                        {/* Card — content only */}
                        <IPDEntryCard
                          entry={transformedEntry}
                          visit={visit}
                          onEdit={() => handleEditEntry(transformedEntry)}
                          onRefresh={() => {
                            if (params.visitId) {
                              fetchVisit(params.visitId);
                              fetchTimelineEntries(parseInt(params.visitId));
                            }
                          }}
                          onCommentsClick={handleCommentsClick}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Admission Form Modal */}
      {showAdmissionForm && resolvedPatientId && resolvedClinicVisitTypeId && (
        <Modal
          width="max-w-2xl"
          title={editingEntry ? "Edit IPD Admission" : "IPD Admission"}
          onClose={handleCancelAdmission}
          modalContent={
            <IPDAdmissionForm
              patientId={resolvedPatientId}
              clinicVisitTypeId={resolvedClinicVisitTypeId}
              initialData={(editingEntry?.data || visit) ? {
                admittingDoctorId:
                  editingEntry?.data?.admittingDoctorId ||
                  editingEntry?.data?.userId ||
                  visit?.userId,
                chiefComplaint:
                  editingEntry?.data?.chiefComplaint ||
                  (visit as any)?.chiefComplaint,
                provisionalDiagnosis:
                  editingEntry?.data?.provisionalDiagnosis ||
                  visit?.provisionalDiagnosis ||
                  [],
                roomId:
                  editingEntry?.data?.roomId ||
                  visit?.meta?.roomId,
                bedId:
                  editingEntry?.data?.bedId ??
                  visit?.meta?.bedId ??
                  null,
                paymentType:
                  editingEntry?.data?.paymentType ||
                  visit?.meta?.paymentType ||
                  "self",
                insuranceProviderId:
                  editingEntry?.data?.insuranceProviderId ??
                  visit?.meta?.insuranceProviderId ??
                  null,
                policyNumber:
                  editingEntry?.data?.policyNumber ??
                  visit?.meta?.policyNumber ??
                  "",
                coverageType:
                  editingEntry?.data?.coverageType ??
                  (editingEntry?.data as any)?.coverage_type ??
                  visit?.meta?.coverageType ??
                  (visit?.meta as any)?.coverage_type ??
                  undefined,
                admissionDate:
                  editingEntry?.data?.admissionDate ||
                  (editingEntry?.data as any)?.admission_date ||
                  visit?.meta?.admissionDate ||
                  (visit?.meta as any)?.admission_date ||
                  (visit?.date ? format(new Date(visit.date), "yyyy-MM-dd") : undefined),
                admissionTime:
                  editingEntry?.data?.admissionTime ||
                  (editingEntry?.data as any)?.admission_time ||
                  visit?.meta?.admissionTime ||
                  (visit?.meta as any)?.admission_time ||
                  (visit?.date ? format(new Date(visit.date), "HH:mm") : undefined),
              } : undefined}
              visitId={visit?.id}
              admissionEntryId={editingEntry?.id}
              onSave={handleAdmissionSaved}
              onCancel={handleCancelAdmission}
            />
          }
        />
      )}

      {/* Entry Form Modal */}
      {showEntryForm && entryType && visit && (
        <IPDEntryForm
          visit={visit}
          entryType={entryType}
          entry={editingEntry}
          onSave={handleEntrySaved}
          onCancel={handleCancelEntry}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          total={0}
          discount={0}
          patientName={`${patient?.firstName || ""} ${patient?.lastName || ""}`}
          patientId={patient?.id}
          sourceType="pathology"
          sourceId={selectedLabOrderId}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            if (visit?.id) {
              fetchVisit(visit.id);
              fetchTimelineEntries(visit.id);
            }
          }}
        />
      )}

      {showLabOrderModal && (
        <Modal
          modalContent={
            <AddLabOrder
              orderId={null}
              visitId={visit?.id}
              patientId={patient?.id}
              hideBillingFields={true}
              isFromIPD={true}
              closeModal={() => setShowLabOrderModal(false)}
              onOrderCreated={(order) => {
                showNotification(
                  "success",
                  `Lab order ${order.orderNumber} created successfully`,
                  "",
                  "topRight"
                );
                setSelectedLabOrderId(order.id);
                setShowLabOrderModal(false);
                if (params.visitId) {
                  fetchVisit(params.visitId);
                  fetchTimelineEntries(parseInt(params.visitId));
                }
              }}
            />
          }
        />
      )}

      {showServiceModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black/50">
          <ServiceModal
            isOpen={showServiceModal}
            onClose={() => setShowServiceModal(false)}
            visitId={visit?.id}
            onServiceAdded={async (savedServices) => {
              if (!visit?.id) {
                showNotification(
                  "error",
                  "Visit not found",
                  "Please ensure visit is loaded before adding services",
                  "topRight"
                );
                setShowServiceModal(false);
                return;
              }

              try {
                // Services are already saved by ServiceModal via updateVisitServices API
                // Backend implements delete-and-add: deletes all existing services and adds only the ones in payload
                // So we just need to update the local visit state with the saved services
                const updatedVisit = {
                  ...visit,
                  services: savedServices,
                };

                // Update the visit in state
                dispatch(changeVisit(updatedVisit));
                setVisit(updatedVisit);

                showNotification(
                  "success",
                  "Services updated successfully",
                  "",
                  "topRight"
                );
                setShowServiceModal(false);

                // Refresh visit and timeline to get latest data from backend
                fetchVisit(visit.id);
                fetchTimelineEntries(visit.id);
              } catch (error: any) {
                console.error("Failed to update visit state with services:", error);
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.data?.message ||
                  "Failed to update services";
                showNotification("error", errorMessage, "", "topRight");
              }
            }}
          />
        </div>
      )}

      {showBillingModal && visit?.id && patient?.id && (
        <IPDBillingModal
          isOpen={showBillingModal}
          onClose={() => setShowBillingModal(false)}
          visitId={visit.id}
          patientId={patient.id}
        />
      )}
      <AntModal
        open={isLabModalOpen}
        footer={null}
        onCancel={() => setIsLabModalOpen(false)}
        width={1100}
        title="Lab Test Reports"
      >
        {patient?.id && (
          <ViewLabOrder
            isModal={true}
            orderId={selectedLabOrderId}
            onClose={() => setIsLabModalOpen(false)}
          />
        )}
      </AntModal>

      {openLabReport && (
        <AntModal
          open={openLabReport}
          onCancel={() => setOpenLabReport(false)}
          footer={null}
          width={1100}
          title="Lab Test Reports"
          destroyOnClose
        >
          <ViewLabOrder
            isModal={true}
            patientId={selectedPatientId ?? undefined}
            onClose={() => setOpenLabReport(false)}
          />
        </AntModal>
      )}
      {/* Comments modal (for doctor rounds) - uses GeneralCommentForm */}
      {showCommentsModal && commentsModalEntry && (
        <Modal
          width="max-w-2xl"
          title="Comments"
          onClose={handleCommentsModalClose}
          modalContent={
            <GeneralCommentForm
              initialData={commentsModalEntry?.data}
              onSave={handleCommentsSave}
              onCancel={handleCommentsModalClose}
            />
          }
        />
      )}

    </div>
  );
};

export default IPDTimeline;


